import { FormikErrors, FormikTouched, useFormik } from 'formik';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useInviteToOrganization } from '@hooks/react-query/mutations/useInviteToOrganization';
import {
  Button,
  ChevronDownIcon,
  Divider,
  Field,
  FieldTypes,
  Modal,
  SelectChangeEvent,
  Typography,
  useBreakpoints,
  useToast,
} from 'ui';
import { RowListWithActions } from '@components/RowListWithActions';
import {
  DropdownType,
  getUserRoleSelectionItems,
  UNASSIGNED_ROLE,
  UserRoleDropdown,
} from '@components/UserRoleDropdown';
import { useLoading } from '@context/LoadingContext';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { capitalizeFirstLetter } from '@utils/strings';
import { InviteToOrganizationPayload } from 'services/ClaimscoreApiService/Invitations/types';
import { CasesPermissions } from 'services/ClaimscoreApiService/Permissions/types';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { FirebaseAuth } from 'services/FirebaseService/auth';
import { StyledListItemSelect } from '../InviteToCaseModal/InviteToCaseModal';
import {
  FieldStackContainer,
  StyledBox,
  StyledBoxContainer,
  StyledButtonContent,
  StyledStack,
  StyledTitleStack,
} from './InviteToOrgModal.styles';
import { ADD_NEW_USER_CONTAINER } from './InviteToOrgModal.testIds';
import { validationSchema } from './utils';

interface InviteFormValues {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  roleName: UserRole | '';
  organizationID: string;
  cases: never[];
}

interface RenderFieldProps {
  readonly name: keyof InviteFormValues;
  readonly label: string;
  readonly type?: FieldTypes;
  readonly disabled?: boolean;
}

interface InviteToOrgModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

enum InviteToOrgSteps {
  BasicInformation,
  AssignCases,
}

const ORGANIZATION_DROPDOWN_TYPE: DropdownType = 'organization';
const CASE_DROPDOWN_TYPE: DropdownType = 'case';

function getFieldErrorMessage(
  touched: FormikTouched<InviteFormValues>,
  errors: FormikErrors<InviteFormValues>,
  fieldName: keyof InviteFormValues,
): string | undefined {
  if (!touched[fieldName]) {
    return undefined;
  }

  const error = errors[fieldName];
  if (Array.isArray(error)) {
    return error[0];
  }
  return capitalizeFirstLetter(error || '');
}

export function InviteToOrgModal({ open, onClose }: InviteToOrgModalProps) {
  const { displayToaster } = useToast();
  const { isLoading, setIsLoading } = useLoading();
  const { isSmUp } = useBreakpoints();
  const { selectedOrganization, userCases, getUserRole } = useUserOrgCase();
  const [selectedView, setSelectedView] = useState(
    InviteToOrgSteps.BasicInformation,
  );
  const [casesToRender, setCasesToRender] = useState<CasesPermissions[]>(
    userCases || [],
  );

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  useEffect(() => {
    FirebaseAuth.isSuperAdmin().then(setIsSuperAdmin);
  }, []);

  useEffect(() => {
    if (selectedOrganization.organizationID) {
      setCasesToRender(
        userCases.filter(
          (userCase) =>
            userCase.organizationID === selectedOrganization.organizationID,
        ),
      );
    }
  }, [selectedOrganization, userCases]);

  const [updatedCases, setUpdatedCases] = useState<CasesPermissions[]>([]);
  const { mutateAsync: inviteToOrganization } = useInviteToOrganization();

  const handleFormSubmit = async (values: InviteFormValues) => {
    if (!values.roleName) {
      displayToaster('Please select a role', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const payload: InviteToOrganizationPayload = {
        ...values,
        roleName: values.roleName,
        organizationID: selectedOrganization.organizationID,
        cases: updatedCases
          .filter(({ roleName }) => roleName !== UNASSIGNED_ROLE.key)
          .map(({ caseID, roleName }) => ({
            caseID,
            role: roleName as UserRole,
          })),
      };
      await inviteToOrganization(payload);
      displayToaster('Invitation successfully sent.', 'success');
    } catch (error: unknown) {
      displayToaster('Invitation could not be sent.', 'error');
    } finally {
      handleOnClose();
      setIsLoading(false);
    }
  };

  const formik = useFormik<InviteFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      roleName: '',
      organizationID: '',
      cases: [],
    },
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  useLayoutEffect(() => {
    if (open && selectedOrganization.organizationName) {
      formik.setFieldValue(
        'companyName',
        selectedOrganization.organizationName,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedOrganization.organizationName]);

  const handleDropdownChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue('roleName', event.target.value);
    },
    [formik],
  );

  const handleRoleChange = useCallback(
    (
      index: number,
      event: SelectChangeEvent<unknown>,
      caseData: CasesPermissions,
    ) => {
      const newRole = event.target.value as UserRole;

      setCasesToRender((prevCases) =>
        prevCases.map((prevCase, i) =>
          i === index ? { ...prevCase, roleName: newRole } : prevCase,
        ),
      );

      setUpdatedCases((prevCases) => {
        const caseFound = prevCases.find(
          (prevCase) => prevCase.caseID === caseData.caseID,
        );

        if (caseFound) {
          return prevCases.map((prevCase) =>
            prevCase.caseID === caseData.caseID
              ? { ...prevCase, roleName: newRole }
              : prevCase,
          );
        }
        return [...prevCases, { ...caseData, roleName: newRole }];
      });
    },
    [],
  );

  const cases = useMemo(() => {
    const userCaseRole = getUserRole(RoleLevel.case);
    const userRoleOptions = getUserRoleSelectionItems(
      userCaseRole as UserRole,
      CASE_DROPDOWN_TYPE,
      isSuperAdmin,
    );
    const options = [UNASSIGNED_ROLE, ...userRoleOptions];
    return casesToRender?.map((userCase, index) => ({
      rowId: userCase.caseID,
      label: userCase.caseName,
      secondaryAction: (
        <StyledListItemSelect
          defaultValue={UNASSIGNED_ROLE.key}
          iconComponent={ChevronDownIcon}
          onChange={(e: SelectChangeEvent<unknown>) =>
            handleRoleChange(index, e, userCase)
          }
          options={options}
        />
      ),
    }));
  }, [casesToRender, getUserRole, handleRoleChange, isSuperAdmin]);

  const renderFormFields = useCallback(() => {
    const { touched, errors, values, handleBlur, handleChange } = formik;

    const renderField = ({
      name,
      label,
      type = FieldTypes.text,
      disabled = false,
    }: RenderFieldProps) => (
      <Field
        disabled={disabled}
        errorMsg={getFieldErrorMessage(touched, errors, name)}
        label={label}
        menuItems={
          name === 'companyName'
            ? [
                {
                  key: selectedOrganization.organizationName,
                  value: selectedOrganization.organizationName,
                },
              ]
            : undefined
        }
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        type={type}
        value={values[name] as string}
      />
    );

    if (selectedView === InviteToOrgSteps.BasicInformation) {
      return (
        <>
          <StyledStack>
            <StyledTitleStack>
              <Typography variant="subtitle">Basic Information</Typography>
              <Typography color="textSecondary" variant="caption">
                Enter the user’s first name, last name, and email to set up
                their profile.
              </Typography>
            </StyledTitleStack>
            <FieldStackContainer>
              <StyledBox>
                {renderField({ name: 'firstName', label: 'First Name' })}
                {renderField({ name: 'lastName', label: 'Last Name' })}
              </StyledBox>
              {renderField({ name: 'email', label: 'Email' })}
            </FieldStackContainer>
          </StyledStack>
          <Divider noMargin />
          <StyledStack>
            <StyledTitleStack>
              <Typography variant="subtitle">Organization</Typography>
              <Typography color="textSecondary" variant="caption">
                Select the user's company and assign their role within the
                organization.
              </Typography>
            </StyledTitleStack>
            {renderField({
              name: 'companyName',
              label: 'Company Name',
              disabled: true,
            })}
            <UserRoleDropdown
              dropdownType={ORGANIZATION_DROPDOWN_TYPE}
              errorMsg={(touched.roleName && errors.roleName) || undefined}
              name="roleName"
              onChange={handleDropdownChange}
              value={values.roleName as UserRole}
            />
          </StyledStack>
        </>
      );
    }

    return (
      <StyledStack>
        <Typography variant="subtitle">Assign Case Roles</Typography>
        <Typography color="textSecondary" variant="caption">
          Select a role for this user in any applicable cases.
        </Typography>
        <StyledBoxContainer>
          <RowListWithActions key="assignCaseRole" rows={cases} />
        </StyledBoxContainer>
      </StyledStack>
    );
  }, [
    cases,
    formik,
    handleDropdownChange,
    selectedOrganization.organizationName,
    selectedView,
  ]);

  const handleContinueClick = useCallback(() => {
    setSelectedView(InviteToOrgSteps.AssignCases);
  }, []);

  const handleOnClose = useCallback(() => {
    formik.resetForm();
    setCasesToRender(userCases);
    setUpdatedCases([]);
    setSelectedView(InviteToOrgSteps.BasicInformation);
    onClose();
  }, [formik, onClose, userCases]);

  const actionButtonGroup = useMemo(() => {
    if (selectedView === InviteToOrgSteps.BasicInformation) {
      return (
        <StyledButtonContent>
          <Button
            disabled={Object.keys(formik.errors).length > 0 || !formik.dirty}
            fullWidth={!isSmUp}
            id="continue-add-new-user-button"
            label="Continue"
            onClick={handleContinueClick}
          />
        </StyledButtonContent>
      );
    }

    return (
      <StyledButtonContent>
        <Button
          fullWidth={!isSmUp}
          id="previous-add-new-user"
          label="Previous"
          onClick={() => setSelectedView(InviteToOrgSteps.BasicInformation)}
          variant="outlined"
        />
        <Button
          fullWidth={!isSmUp}
          label="Send Invite"
          loading={isLoading}
          loadingPosition="start"
          onClick={() => formik.handleSubmit()}
          type="submit"
        />
      </StyledButtonContent>
    );
  }, [formik, handleContinueClick, isLoading, isSmUp, selectedView]);

  return (
    <Modal
      actionButtons={actionButtonGroup}
      data-testid={ADD_NEW_USER_CONTAINER}
      modalClose={onClose}
      onClose={handleOnClose}
      open={open}
      title="Invite New User"
    >
      {renderFormFields()}
    </Modal>
  );
}
